import React, { memo, useCallback, useMemo, useRef } from "react";
import { DataGrid } from "@toddle-edu/ds-data-grid";
import classes from "./Grid.scss";
import classNames from "classnames";
import PropTypes from "prop-types";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { useStandardsTable } from "StandardsTable/context/StandardsTableContext";
import {
  getDataGridHeaders as _getDataGridHeaders,
  getEdgesFromNodeMap as _getEdgesFromNodeMap,
  getLoaderData,
} from "./modules/Utils";
import _ from "lodash";
import { DATA_GRID_COLUMN_TYPES } from "StandardsTable/modules/constants";
import { Button, EmptyState } from "@toddle-edu/ds-web";
import { NoFilterResultIllustration } from "@toddle-edu/ds-theme";
import { useI18n, useContainerWidth } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";

const TEST_ID_PREFIX = "standardsTable-grid";

const EMPTY_OBJECT = {};
const ROW_MARKERS_DARK_THEME = {
  bgHeader: "#333",
};

const Grid = props => {
  const {
    gridClass,
    onCellEdit,
    headerHeight,
    onRowHandlerClick,
    dataGridHeight,
    dataGridWidth,
    freezeColumns,
    tooltipDelayTime,
    focusCell,
    showHoverState,
    rowMarkers,
    getEdgesFromNodeMap,
    getDataGridHeaders,
    blurSelectionOnOutsideClick,
    columnSelectionEnabled,
    initialSelectedRows,
    onRowSelectionChange,
    enableEditHighlight,
    fillColumn,
    resetFilters,
    showLoader,
    emptyState,
    isDarkHeader,
    clearSelection,
    onAppendRowAtEnd,
    cellSelectionBorderColor,
    handlePasteFailure,
    emptyScreenTitle,
    enableCollapse,
    scrollCellData,
    onVisibleRowsChange,
  } = props;
  const containerRef = useRef(null);
  const parentContainerWidth = useContainerWidth({ containerRef });

  // Collapse is enabled by default; parents may disable it via enableCollapse={false}.
  const enableCollapseInGrid = enableCollapse ?? true;

  const dispatch = useDispatch();
  const dispatchSetToastMsg = params => dispatch(setToastMsg(params));

  const { t } = useI18n(["scopeAndSequence", "common"]);

  const containerClass = classNames(classes.container, gridClass);

  const {
    nodeMap,
    columns,
    properties,
    actionHandlerFn,
    gridProps: contextGridProps,
  } = useStandardsTable();

  // Use grid props from context if not provided directly
  const finalScrollCellData =
    scrollCellData || contextGridProps?.scrollCellData;
  const finalOnVisibleRowsChange =
    onVisibleRowsChange || contextGridProps?.onVisibleRowsChange;

  const nodeMapRef = useRef(nodeMap);
  const propertiesRef = useRef(properties);
  nodeMapRef.current = nodeMap;
  propertiesRef.current = properties;

  const handleEdit = useCallback(
    params => {
      if (onCellEdit) {
        onCellEdit({ params, nodeMap, properties });
      }
    },
    [nodeMap, onCellEdit, properties]
  );

  //memoized to avoid cancelling of previous debounce
  const addRowHandler = useMemo(
    () =>
      _.debounce(params => {
        onRowHandlerClick({
          ...params,
          nodeMap: nodeMapRef.current,
          properties: propertiesRef.current,
        });
      }, 200),
    [onRowHandlerClick]
  );
  const isAddRowHandlerEnabled = !!onRowHandlerClick;
  const headers = useMemo(() => {
    return getDataGridHeaders({
      properties,
      columns,
      parentContainerWidth,
      fillColumn,
      isRowMarkersEnabled: rowMarkers !== "none",
      isAddRowHandlerEnabled,
      actionHandlerFn,
      isDarkHeader,
    });
  }, [
    getDataGridHeaders,
    isAddRowHandlerEnabled,
    properties,
    columns,
    parentContainerWidth,
    fillColumn,
    rowMarkers,
    actionHandlerFn,
    isDarkHeader,
  ]);

  const data = useMemo(
    () =>
      showLoader ? getLoaderData({ columns }) : getEdgesFromNodeMap(nodeMap),
    [columns, getEdgesFromNodeMap, nodeMap, showLoader]
  );

  const renderGrid = () => {
    if (_.isEmpty(data))
      return emptyState ? (
        emptyState
      ) : (
        <EmptyState
          illustration={NoFilterResultIllustration}
          title={
            emptyScreenTitle
              ? emptyScreenTitle
              : t("scopeAndSequence:no_standards_found")
          }
          subtitle={t("scopeAndSequence:no_standards_found_subtitle")}
          primaryButton={
            <Button
              type="outlined"
              variant="neutral"
              onClick={resetFilters}
              testId={`${TEST_ID_PREFIX}-resetFilters`}
            >
              {t("common:reset_filter_plural")}
            </Button>
          }
        />
      );

    return (
      <DataGrid
        data={data}
        headers={headers}
        onCellEdit={handleEdit}
        headerHeight={headerHeight}
        onRowHandlerClick={onRowHandlerClick ? addRowHandler : undefined}
        dataGridHeight={dataGridHeight}
        dataGridWidth={dataGridWidth}
        componentName="sns"
        freezeColumns={freezeColumns}
        tooltipDelayTime={tooltipDelayTime}
        focusCell={focusCell}
        showHoverState={showHoverState}
        rowMarkers={rowMarkers}
        blurSelectionOnOutsideClick={blurSelectionOnOutsideClick}
        columnSelectionEnabled={columnSelectionEnabled}
        initialSelectedRows={initialSelectedRows}
        onRowSelectionChange={onRowSelectionChange}
        enableEditHighlight={enableEditHighlight}
        rowMarkerTheme={isDarkHeader ? ROW_MARKERS_DARK_THEME : EMPTY_OBJECT}
        clearSelection={clearSelection}
        onAppendRowAtEnd={onAppendRowAtEnd}
        selectionBorderColor={cellSelectionBorderColor}
        onPasteFailure={handlePasteFailure}
        setToastMsg={dispatchSetToastMsg}
        enableCollapse={enableCollapseInGrid}
        onVisibleRowsChange={finalOnVisibleRowsChange}
        scrollCellData={finalScrollCellData}
      />
    );
  };
  return (
    <div className={containerClass} ref={containerRef}>
      {renderGrid()}
    </div>
  );
};

Grid.propTypes = {
  onCellEdit: PropTypes.func,
  headerHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onRowHandlerClick: PropTypes.func,
  dataGridHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataGridWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  freezeColumns: PropTypes.number,
  tooltipDelayTime: PropTypes.number,
  focusCell: PropTypes.object,
  showHoverState: PropTypes.bool,
  rowMarkers: PropTypes.string,
  className: PropTypes.string,
  getEdgesFromNodeMap: PropTypes.func,
  getDataGridHeaders: PropTypes.func,
  blurSelectionOnOutsideClick: PropTypes.bool,
  columnSelectionEnabled: PropTypes.bool,
  initialSelectedRows: PropTypes.arrayOf(PropTypes.string),
  onRowSelectionChange: PropTypes.func,
  enableEditHighlight: PropTypes.bool,
  fillColumn: PropTypes.oneOf(Object.values(DATA_GRID_COLUMN_TYPES)),
  resetFilters: PropTypes.func,
  showLoader: PropTypes.bool,
  emptyState: PropTypes.node,
  isDarkHeader: PropTypes.bool,
  clearSelection: PropTypes.bool,
  onAppendRowAtEnd: PropTypes.func,
  handlePasteFailure: PropTypes.func,
  emptyScreenTitle: PropTypes.string,
};

Grid.defaultProps = {
  headerHeight: 44,
  dataGridHeight: "100%",
  dataGridWidth: "100%",
  tooltipDelayTime: 300,
  showHoverState: false,
  rowMarkers: "none",
  freezeColumns: 0,
  getEdgesFromNodeMap: _getEdgesFromNodeMap,
  getDataGridHeaders: _getDataGridHeaders,
  blurSelectionOnOutsideClick: true,
  columnSelectionEnabled: false,
  enableEditHighlight: false,
  fillColumn: DATA_GRID_COLUMN_TYPES.TITLE,
  resetFilters: () => {},
  showLoader: false,
  isDarkHeader: false,
  clearSelection: false,
  handlePasteFailure: () => {},
  emptyScreenTitle: "",
};

export default memo(Grid);
